import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { format, subMonths } from 'date-fns';
import { Droplets, AlertCircle, RefreshCw } from 'lucide-react';
import useMarketData from '../hooks/useMarketData.js';

const FLOW_DESTINATIONS = [
  { key: 'mmf', label: 'Money Market Funds', color: '#60a5fa', unit: 'B' },
  { key: 'sp500', label: 'S&P 500', color: '#22c55e', unit: '' },
  { key: 'gold', label: 'Gold', color: '#f0c040', unit: '/oz' },
  { key: 'btc', label: 'Bitcoin', color: '#f7931a', unit: '' },
];

export default function LiquidityFlowMonitor() {
  const market = useMarketData();
  const { btc, gold, fred, equityHistory } = market;

  const mmfSeries = fred.mmf || [];
  const rrpSeries = fred.rrp || [];
  const fedBalSeries = fred.fedBal || [];
  const ffrSeries = fred.ffr || [];
  const m2Series = fred.m2 || [];
  const sp500Series = equityHistory.sp500 || [];
  const goldSeries = gold.history || [];
  const btcSeries = btc.history || [];

  const noData = !mmfSeries.length && !m2Series.length;
  const coreLoading = fred.status === 'loading';

  const analysis = useMemo(() => {
    if (!m2Series.length) return null;

    // Latest values
    const latestMMF = mmfSeries.length ? mmfSeries[mmfSeries.length - 1] : null;
    const latestRRP = rrpSeries.length ? rrpSeries[rrpSeries.length - 1] : null;
    const latestFedBal = fedBalSeries.length ? fedBalSeries[fedBalSeries.length - 1] : null;
    const latestFFR = ffrSeries.length ? ffrSeries[ffrSeries.length - 1] : null;
    const latestM2 = m2Series.length ? m2Series[m2Series.length - 1] : null;

    // MMF change (latest vs 3 months ago)
    const mmf3mAgo = mmfSeries.length > 12 ? mmfSeries[mmfSeries.length - 13] : null;
    const mmfChange = latestMMF && mmf3mAgo ? latestMMF.value - mmf3mAgo.value : null;

    // RRP drain (peak vs now)
    const rrpPeak = rrpSeries.length ? Math.max(...rrpSeries.map(p => p.value)) : null;
    const rrpDrained = rrpPeak && latestRRP ? ((rrpPeak - latestRRP.value) / rrpPeak * 100) : null;

    // Fed balance sheet change (6mo)
    const fedBal6mAgo = fedBalSeries.length > 24 ? fedBalSeries[fedBalSeries.length - 25] : null;
    const fedBalChange = latestFedBal && fedBal6mAgo ? ((latestFedBal.value / fedBal6mAgo.value - 1) * 100) : null;

    // MMF yield proxy (FFR ≈ MMF yield in the current regime)
    const mmfYield = latestFFR?.value ?? null;

    // Rotation threshold: MMF yields below 2.5-3% historically trigger rotation
    const rotationThreshold = 2.5;
    const yieldAboveThreshold = mmfYield != null ? mmfYield - rotationThreshold : null;

    // Normalized flow chart (2Y lookback)
    const cutoff = subMonths(new Date(), 24);
    const chartData = buildFlowChart(m2Series, mmfSeries, sp500Series, goldSeries, btcSeries, cutoff);

    // Compute who's absorbing the M2 growth (YoY changes)
    const absorption = computeAbsorption(mmfSeries, sp500Series, goldSeries, btcSeries);

    return {
      latestMMF, latestRRP, latestFedBal, latestFFR, latestM2,
      mmfChange, rrpDrained, fedBalChange, mmfYield,
      rotationThreshold, yieldAboveThreshold,
      chartData, absorption,
    };
  }, [m2Series, mmfSeries, rrpSeries, fedBalSeries, ffrSeries, sp500Series, goldSeries, btcSeries]);

  if (coreLoading && noData) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-24 rounded-xl bg-slate-900 border border-slate-800" />
        <div className="h-72 rounded-2xl bg-slate-900 border border-slate-800" />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-300">Liquidity flow data unavailable</h3>
            <button onClick={() => { market.refetch.fred(); market.refetch.equityHistory(); }}
                    className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded bg-red-500/20 border border-red-500/40 text-red-300">
              <RefreshCw className="w-3 h-3" /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    latestMMF, latestRRP, latestFedBal,
    mmfChange, rrpDrained, fedBalChange, mmfYield,
    rotationThreshold, yieldAboveThreshold,
    chartData, absorption,
  } = analysis;

  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-blue-500/[0.03] via-slate-900/60 to-emerald-500/[0.03] backdrop-blur-xl p-4 md:p-6">
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="relative space-y-5">

        {/* Header */}
        <div>
          <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Droplets className="w-5 h-5 text-blue-400" />
            Liquidity Flow Monitor
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Where is M2 going? Track money market funds, risk assets, and rotation signals in real time
          </p>
        </div>

        {/* Liquidity Plumbing — key reservoirs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <ReservoirCard
            label="Money Market Funds"
            value={latestMMF ? `$${(latestMMF.value / 1000).toFixed(2)}T` : '—'}
            sub={mmfChange != null ? `${mmfChange >= 0 ? '+' : ''}$${mmfChange.toFixed(0)}B (3mo)` : null}
            subTone={mmfChange != null ? (mmfChange > 50 ? 'inflow' : mmfChange < -50 ? 'outflow' : 'flat') : null}
            detail="$7.8T parking lot — drains when rates fall"
            color="#60a5fa"
          />
          <ReservoirCard
            label="Reverse Repo"
            value={latestRRP ? `$${latestRRP.value.toFixed(0)}B` : '—'}
            sub={rrpDrained != null ? `${rrpDrained.toFixed(0)}% drained from peak` : null}
            subTone="outflow"
            detail={latestRRP && latestRRP.value < 50 ? 'Nearly empty — liquidity already deployed' : 'Draining into T-bills and risk assets'}
            color="#818cf8"
          />
          <ReservoirCard
            label="Fed Balance Sheet"
            value={latestFedBal ? `$${(latestFedBal.value / 1e6).toFixed(2)}T` : '—'}
            sub={fedBalChange != null ? `${fedBalChange >= 0 ? '+' : ''}${fedBalChange.toFixed(1)}% (6mo)` : null}
            subTone={fedBalChange != null ? (fedBalChange > 1 ? 'inflow' : fedBalChange < -1 ? 'outflow' : 'flat') : null}
            detail={fedBalChange != null && fedBalChange < -1 ? 'QT active — liquidity withdrawing' : 'Balance sheet stable/expanding'}
            color="#a78bfa"
          />
          <ReservoirCard
            label="MMF Yield (≈FFR)"
            value={mmfYield != null ? `${mmfYield.toFixed(2)}%` : '—'}
            sub={yieldAboveThreshold != null ? `${yieldAboveThreshold.toFixed(1)}pp above rotation trigger` : null}
            subTone={yieldAboveThreshold != null ? (yieldAboveThreshold > 1.5 ? 'parked' : yieldAboveThreshold > 0 ? 'warming' : 'rotating') : null}
            detail={`Rotation trigger: <${rotationThreshold}% (cash no longer competitive)`}
            color="#fbbf24"
          />
        </div>

        {/* Rotation Trigger Gauge */}
        {mmfYield != null && (
          <RotationGauge yield_={mmfYield} threshold={rotationThreshold} mmfTotal={latestMMF?.value} />
        )}

        {/* Flow Chart: normalized YoY growth by destination */}
        {chartData.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
              YoY Growth by Destination (where M2 is flowing)
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} tickLine={false}
                         tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`} />
                  {FLOW_DESTINATIONS.map(({ key, color, label }) => (
                    <Line key={key} type="monotone" dataKey={key} stroke={color} strokeWidth={2}
                          dot={false} name={label} connectNulls={false} />
                  ))}
                  <Tooltip
                    cursor={{ stroke: 'rgba(255,255,255,0.18)', strokeWidth: 1, strokeDasharray: '3 3' }}
                    content={<FlowTooltip />}
                  />
                  <Legend wrapperStyle={{ paddingTop: 8, fontSize: 11 }} iconType="line"
                          formatter={(v) => <span className="text-slate-300">{v}</span>} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Absorption breakdown */}
        {absorption && (
          <AbsorptionBar absorption={absorption} />
        )}
      </div>
    </section>
  );
}

function RotationGauge({ yield_, threshold, mmfTotal }) {
  const pct = Math.min(100, Math.max(0, ((yield_ - threshold) / (6 - threshold)) * 100));
  const rotationPotential = mmfTotal ? (mmfTotal * 0.25 / 1000).toFixed(1) : null;
  const isClose = yield_ < threshold + 1;

  return (
    <div className={`rounded-xl border p-4 ${isClose ? 'border-emerald-500/30 bg-emerald-500/[0.05]' : 'border-white/10 bg-slate-900/40'}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Rotation Trigger Distance</div>
          <div className="text-xs text-slate-300 mt-0.5">
            MMF yield must fall to ~{threshold}% for cash to become uncompetitive
          </div>
        </div>
        <div className="text-right">
          <div className={`text-lg font-bold font-mono tabular-nums ${isClose ? 'text-emerald-400' : 'text-amber-400'}`}>
            {(yield_ - threshold).toFixed(2)}pp
          </div>
          <div className="text-[10px] text-slate-500">above trigger</div>
        </div>
      </div>
      <div className="relative w-full h-3 bg-slate-800 rounded-full overflow-hidden">
        <div className="absolute inset-0 flex">
          <div className="bg-emerald-500/30 h-full" style={{ width: `${100 - pct}%` }} />
          <div className="bg-amber-500/30 h-full" style={{ width: `${pct}%` }} />
        </div>
        <div className="absolute top-0 h-full w-0.5 bg-white/50" style={{ left: `${100 - pct}%` }} />
      </div>
      <div className="flex justify-between text-[10px] text-slate-600 mt-1 font-mono">
        <span>{threshold}% (rotate)</span>
        <span>Current: {yield_.toFixed(2)}%</span>
        <span>6%+ (fully parked)</span>
      </div>
      {rotationPotential && (
        <p className="text-[11px] text-slate-400 mt-2">
          If 25% of MMF assets rotate out → <span className="text-emerald-400 font-bold">${rotationPotential}T</span> flood into risk assets
        </p>
      )}
    </div>
  );
}

function AbsorptionBar({ absorption }) {
  const total = Object.values(absorption).reduce((s, v) => s + Math.max(0, v), 0) || 1;
  const items = FLOW_DESTINATIONS.map(({ key, label, color }) => ({
    key, label, color,
    value: absorption[key] ?? 0,
    pct: Math.max(0, (absorption[key] ?? 0)) / total * 100,
  })).sort((a, b) => b.value - a.value);

  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">
        Who's Absorbing M2 Growth (YoY performance)
      </div>
      <div className="w-full h-4 rounded-full overflow-hidden flex bg-slate-800">
        {items.filter(i => i.value > 0).map(({ key, color, pct }) => (
          <div key={key} className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        {items.map(({ key, label, color, value }) => (
          <div key={key} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
            <div className="min-w-0">
              <span className="text-[10px] text-slate-400 truncate block">{label}</span>
              <span className="text-xs font-mono font-bold tabular-nums" style={{ color }}>
                {value >= 0 ? '+' : ''}{value.toFixed(0)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReservoirCard({ label, value, sub, subTone, detail, color }) {
  const toneColors = {
    inflow: 'text-blue-400',
    outflow: 'text-emerald-400',
    flat: 'text-slate-400',
    parked: 'text-amber-400',
    warming: 'text-blue-400',
    rotating: 'text-emerald-400',
  };

  return (
    <div className="px-3 py-2.5 bg-slate-900/40 border border-white/10 rounded-lg">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 truncate">{label}</div>
      <div className="font-mono font-bold tabular-nums text-lg" style={{ color }}>{value}</div>
      {sub && <div className={`text-[10px] font-mono mt-1 ${toneColors[subTone] || 'text-slate-500'}`}>{sub}</div>}
      {detail && <p className="text-[10px] text-slate-600 mt-1 leading-snug">{detail}</p>}
    </div>
  );
}

function FlowTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-slate-950/95 backdrop-blur-md px-4 py-3 shadow-xl text-xs font-mono tabular-nums">
      <div className="text-slate-200 font-semibold mb-2 font-sans">{row.date}</div>
      <div className="space-y-1">
        {FLOW_DESTINATIONS.map(({ key, label, color }) => {
          const v = row[key];
          if (v == null) return null;
          return (
            <div key={key} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: color }} />
                <span className="text-slate-400 font-sans">{label}:</span>
              </div>
              <span className="text-slate-100">{v >= 0 ? '+' : ''}{v.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function monthKey(d) { return `${d.getFullYear()}-${d.getMonth()}`; }

function buildFlowChart(m2Series, mmfSeries, sp500Series, goldSeries, btcSeries, cutoff) {
  const computeYoYMap = (series) => {
    const byKey = new Map();
    for (const p of series || []) {
      if (p?.date) byKey.set(`${p.date.getFullYear()}-${p.date.getMonth()}`, p.value);
    }
    const out = new Map();
    for (const p of series || []) {
      if (!p?.date || !Number.isFinite(p.value) || p.value <= 0) continue;
      if (p.date < cutoff) continue;
      const priorKey = `${p.date.getFullYear() - 1}-${p.date.getMonth()}`;
      const prior = byKey.get(priorKey);
      if (prior && prior > 0) {
        out.set(monthKey(p.date), ((p.value / prior - 1) * 100));
      }
    }
    return out;
  };

  const mmfYoY = computeYoYMap(mmfSeries);
  const sp500YoY = computeYoYMap(sp500Series);
  const goldYoY = computeYoYMap(goldSeries);
  const btcYoY = computeYoYMap(btcSeries);

  const allKeys = new Set([...mmfYoY.keys(), ...sp500YoY.keys(), ...goldYoY.keys(), ...btcYoY.keys()]);
  const sorted = [...allKeys].sort();

  return sorted.map(k => {
    const [y, m] = k.split('-').map(Number);
    return {
      date: format(new Date(y, m, 1), 'MMM yy'),
      mmf: mmfYoY.get(k) ?? null,
      sp500: sp500YoY.get(k) ?? null,
      gold: goldYoY.get(k) ?? null,
      btc: btcYoY.get(k) ?? null,
    };
  });
}

function computeAbsorption(mmfSeries, sp500Series, goldSeries, btcSeries) {
  const yoy = (series) => {
    if (!series || series.length < 13) return null;
    const latest = series[series.length - 1]?.value;
    const prior = series[series.length - 13]?.value;
    if (!latest || !prior || prior <= 0) return null;
    return ((latest / prior - 1) * 100);
  };

  return {
    mmf: yoy(mmfSeries),
    sp500: yoy(sp500Series),
    gold: yoy(goldSeries),
    btc: yoy(btcSeries),
  };
}
